import { useCallback, useRef, useState } from 'react';
import { message } from 'antd';
import { accountIdentity, findTabKeyForAccount } from '../utils/accountStructure/accountSearch';

/**
 * Page-level "go to account": switches to the tab that shows the account and
 * publishes a `focusRequest` the tables react to (see useLocateRow).
 *
 * @returns {{ focusRequest, locateAccount, clearLocate }}
 */
export function useAccountLocator({ accountStructure, activeKey, setActiveKey }) {
    const [focusRequest, setFocusRequest] = useState(null);

    // Read through refs so the returned callback stays stable for the tables.
    const latest = useRef({ accountStructure, activeKey });
    latest.current = { accountStructure, activeKey };

    const locateAccount = useCallback((account) => {
        const identity = accountIdentity(account);
        if (!identity) return;

        const { accountStructure: structure, activeKey: current } = latest.current;
        const tabKey = findTabKeyForAccount(structure, identity, current);
        if (!tabKey) {
            message.warning(`Account ${account.account?.account_number ?? account.account_number} is not in the structure`);
            return;
        }

        if (tabKey !== current) setActiveKey(tabKey);
        // A new object every time, so locating the same account again re-triggers.
        setFocusRequest({ identity });
    }, [setActiveKey]);

    const clearLocate = useCallback(() => setFocusRequest(null), []);

    return { focusRequest, locateAccount, clearLocate };
}
